import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Loader2, ArrowLeft, Save, Trash2 } from 'lucide-react';
import api from "../../../../api/axios";

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const STORAGE_URL = `${process.env.REACT_APP_BACKEND_URL}/storage/`;
  const STORAGE_URL_MAIN = `${process.env.REACT_APP_BACKEND_URL}`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Category States
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState('');

  const [formData, setFormData] = useState({
    title: '', brand: '', price: '', old_price: '', 
    description: '', stock: '', category_id: '',
    main_image: null, gallery: [],
    promos: 0 
  });

  const [mainPreview, setMainPreview] = useState(null);
  const [existingGallery, setExistingGallery] = useState([]); 
  const [newGalleryPreviews, setNewGalleryPreviews] = useState([]); 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          api.get('/categories'),
          api.get('/all_products') 
        ]);

        const allCategories = catRes.data.data;
        setCategories(allCategories);

        const product = prodRes.data.data.find(p => p.id === parseInt(id));
        
        if (product) {
          // Recursive search to find the category object and its parent
          const findCategoryInfo = (tree, targetId) => {
            for (let cat of tree) {
              if (Number(cat.id) === Number(targetId)) return { current: cat, parent: null };
              if (cat.children && cat.children.length > 0) {
                for (let child of cat.children) {
                  if (Number(child.id) === Number(targetId)) return { current: child, parent: cat };
                }
              }
            }
            return null;
          };

          const catInfo = findCategoryInfo(allCategories, product.category_id);

          // Update Main Form Data
          setFormData({
            title: product.title || '',
            brand: product.brand || '',
            price: product.price || '',
            old_price: product.old_price || '',
            description: product.description || '',
            stock: product.stock || '',
            category_id: String(product.category_id), // MUST BE STRING
            gallery: [],
            promos: Number(product.promos) === 1 ? 1 : 0,
          });

          // Handle the Selects auto-fill
          if (catInfo) {
            if (catInfo.parent) {
              // Product is in a Sub-Category
              setSelectedParentId(String(catInfo.parent.id));
              setSubCategories(catInfo.parent.children || []);
            } else {
              // Product is in a Main Category
              setSelectedParentId(String(catInfo.current.id));
              setSubCategories(catInfo.current.children || []);
            }
          }
          
          setMainPreview(`${STORAGE_URL_MAIN}${product.main_image}`);
          setExistingGallery(product.images || []);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, STORAGE_URL_MAIN]);

  const handleParentCategoryChange = (e) => {
    const parentId = e.target.value;
    setSelectedParentId(parentId);
    
    const selectedCat = categories.find(c => String(c.id) === String(parentId));
    
    if (selectedCat && selectedCat.children) {
      setSubCategories(selectedCat.children);
    } else {
      setSubCategories([]);
    }

    // Default category_id to parent if no sub-cat selected yet
    setFormData({ ...formData, category_id: parentId });
  };


  
  const handleDeleteExistingImage = async (imageId) => {
    if (!window.confirm("Supprimer cette image de la galerie ?")) return;
    try {
      const res = await api.delete(`/product-images/${imageId}`);
      if (res.data.success) {
        setExistingGallery(prev => prev.filter(img => img.id !== imageId));
      }
    } catch (err) {
      alert("Erreur de suppression");
    }
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, main_image: file });
      setMainPreview(URL.createObjectURL(file));
    }
  };

  const handleNewGallery = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...files] }));
    const previews = files.map(file => URL.createObjectURL(file));
    setNewGalleryPreviews(prev => [...prev, ...previews]);
  };




  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = new FormData();
    data.append('_method', 'POST'); 

    data.append('title', formData.title);
    data.append('brand', formData.brand);
    data.append('price', formData.price);
    data.append('old_price', formData.old_price);
    data.append('description', formData.description);
    data.append('stock', formData.stock);
    data.append('category_id', formData.category_id);
    data.append('promos', formData.promos === 1 ? 1 : 0);

    if (formData.main_image instanceof File) {
      data.append('main_image', formData.main_image);
    }
  
    if (formData.gallery && formData.gallery.length > 0) {
      formData.gallery.forEach((file) => data.append('gallery[]', file));
    }
  
    try {
      await api.post(`/products/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert("Mis à jour avec succès !");
      navigate('/admin/dashboard/products');
    } catch (err) {
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ffaa00]" size={40} /></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto p-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#8A8A89] font-black text-xs mb-8 uppercase hover:text-black transition-all">
        <ArrowLeft size={16} /> Retour
      </button>

      <form onSubmit={handleSubmit} className="bg-white rounded-[2.5rem] shadow-2xl p-10 border border-gray-100 space-y-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          
          <div className="space-y-6">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#ffaa00]">Détails du produit</h3>
            
            <input required value={formData.title} className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold" onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Titre" />
            
            <div className="grid grid-cols-1 gap-4">
               <input value={formData.brand} className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold" onChange={e => setFormData({...formData, brand: e.target.value})} placeholder="Marque" />
               
               <div className="grid grid-cols-2 gap-4">
                  {/* Parent Select */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase ml-2 text-gray-400">Catégorie Parente</label>
                    <select required value={selectedParentId} className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm" onChange={handleParentCategoryChange}>
                      <option value="">Sélectionner...</option>
                      {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                    </select>
                  </div>

                  {/* Sub-Category Select */}
                  <div className="space-y-1">
                    <label className="text-[9px] font-black uppercase ml-2 text-gray-400">Sous-Catégorie</label>
                    <select 
                      disabled={subCategories.length === 0}
                      value={String(formData.category_id)}
                      className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold text-sm disabled:opacity-50" 
                      onChange={e => setFormData({...formData, category_id: e.target.value})}
                    >
                      <option value="">{subCategories.length > 0 ? "Choisir sous-catégorie" : "N/A"}</option>
                      {subCategories.map(sc => <option key={sc.id} value={String(sc.id)}>{sc.name}</option>)}
                    </select>
                  </div>
               </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <input required type="number" value={formData.price} className="p-4 bg-gray-50 rounded-2xl outline-none font-bold border-2 border-[#ffaa00]/20" onChange={e => setFormData({...formData, price: e.target.value})} placeholder="Prix" />
              <input type="number" value={formData.old_price} className="p-4 bg-gray-50 rounded-2xl outline-none font-bold text-gray-400" onChange={e => setFormData({...formData, old_price: e.target.value})} placeholder="Ancien Prix" />
              <input required type="number" value={formData.stock} className="p-4 bg-gray-50 rounded-2xl outline-none font-bold" onChange={e => setFormData({...formData, stock: e.target.value})} placeholder="Stock" />
            </div>

            <textarea value={formData.description} rows="4" className="w-full p-4 bg-gray-50 rounded-2xl outline-none font-bold" onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Description..." />
          </div>

          {/* Media Section remains the same as before */}
          <div className="space-y-8">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89]">Images</h3>
            <label className="block h-48 border-2 border-dashed border-gray-100 rounded-[2rem] relative overflow-hidden cursor-pointer group hover:border-[#ffaa00] transition-all">
                <img src={mainPreview} className="w-full h-full object-contain" alt="Preview" />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white font-bold text-xs uppercase">Changer</div>
                <input type="file" className="hidden" onChange={handleMainImageChange} />
            </label>



            <div className="space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-2">Galerie Secondaire</p>
              <div className="grid grid-cols-3 gap-4">
                <AnimatePresence>
                  {existingGallery.map((img) => (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} key={img.id} className="relative aspect-square rounded-2xl overflow-hidden group border border-gray-100 shadow-sm">
                      <img src={`${STORAGE_URL}${img.image_path}`} className="w-full h-full object-cover" alt="Gallery" />
                      <button type="button" onClick={() => handleDeleteExistingImage(img.id)} className="absolute inset-0 bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Trash2 size={20} />
                      </button>
                    </motion.div>
                  ))}
                  {newGalleryPreviews.map((src, i) => (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={`new-${i}`} className="relative aspect-square rounded-2xl overflow-hidden border-2 border-[#ffaa00]">
                      <img src={src} className="w-full h-full object-cover" alt="New Preview" />
                    </motion.div>
                  ))}
                </AnimatePresence>

                <label className="aspect-square border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:bg-orange-50 transition-all text-gray-400">
                  <Plus size={24} />
                  <input type="file" multiple className="hidden" onChange={handleNewGallery} />
                </label>
              </div>
            </div>

            


          </div>
        </div>

        <button type="submit" disabled={saving} className="w-full py-6 bg-[#ffaa00] text-white rounded-[1.5rem] font-black uppercase tracking-[0.3em] shadow-xl hover:bg-black transition-all flex items-center justify-center gap-3">
          {saving ? <Loader2 className="animate-spin" /> : <><Save size={20} /> Mettre à jour</>}
        </button>
      </form>
    </motion.div>
  );
};

export default EditProduct;